import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import { useDispatch } from "react-redux";

import AuthSplitLayout from "../../Components/Auth/AuthSplitLayout";
import GoogleAuthButton from "../../Components/Common/GoogleAuthButton";
import withRouter from "../../Components/Common/withRouter";
import {
  digitsOnly,
  formatCountdown,
  isAuthRateLimited,
  resolveAuthClientError,
} from "../../helpers/authClientError";
import {
  getPreferEmailCode,
  setPreferEmailCode,
} from "../../helpers/authLoginPreference";
import {
  getPreferredTenantId,
  isMembershipEnterable,
  StudioMembership,
} from "../../helpers/identity_storage";
import { resolvePostAuthRedirect } from "../../helpers/postAuthRedirect";
import { loginSuccess } from "../../slices/auth/login/reducer";
import {
  getAuthSession,
  login as authLogin,
  requestEmailCode,
  selectTenant,
  verifyEmailCode,
  LoginResult,
} from "../../services/authService";
import { ApiResponse } from "../../types/api";
import { storeGoogleSignupDraft } from "./GoogleCallback";

const OTP_RESEND_COOLDOWN_SECONDS = 60;

type LoginErrorAction = "none" | "no_membership" | "account_not_found";

const Login = (props: any) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorAction, setErrorAction] = useState<LoginErrorAction>("none");
  const [useEmailCode, setUseEmailCode] = useState<boolean>(() =>
    getPreferEmailCode()
  );
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [emailHint, setEmailHint] = useState<string>("");
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [showSpamHint, setShowSpamHint] = useState<boolean>(false);

  const clearLoginError = () => {
    setErrorMessage("");
    setErrorAction("none");
  };

  const presentLoginError = (
    message: string,
    action: LoginErrorAction = "none"
  ) => {
    setErrorMessage(message);
    setErrorAction(action);
    // Alert + CTA já cobre o feedback; toast repetiria a mesma frase.
    if (action === "none") {
      toast.error(message);
    }
  };

  const resolveErrorAction = (
    code?: string,
    status?: number
  ): LoginErrorAction => {
    if (code === "NO_ACTIVE_MEMBERSHIP") {
      return "no_membership";
    }

    if (status === 404) {
      return "account_not_found";
    }

    return "none";
  };

  const postLoginPath = (): string => {
    const location = props.router?.location;
    const returnQuery = new URLSearchParams(location?.search || "").get("return");

    return resolvePostAuthRedirect(location?.state, returnQuery);
  };

  const finishLogin = async (
    response: ApiResponse<LoginResult>
  ): Promise<boolean> => {
    if (
      response.success &&
      response.data &&
      "type" in response.data &&
      response.data.type === "studio_creation_required"
    ) {
      storeGoogleSignupDraft({
        identity_token: response.data.identity_token,
        email: response.data.email,
        name: response.data.name || "",
      });

      if (response.message) {
        toast.success(response.message);
      }

      props.router.navigate("/register?continue=1");
      return true;
    }

    if (
      response.success &&
      response.data &&
      "type" in response.data &&
      response.data.type === "membership_selection_required"
    ) {
      const preferredTenantId = getPreferredTenantId();
      const memberships = (response.data.memberships ||
        []) as StudioMembership[];
      const preferred = preferredTenantId
        ? memberships.find(
            (item) =>
              item.tenant_id === preferredTenantId &&
              isMembershipEnterable(item)
          )
        : null;

      if (preferred) {
        const selected = await selectTenant(preferred.tenant_id);
        const session = getAuthSession();

        if (selected.success && session) {
          if (selected.message || response.message) {
            toast.success(selected.message || response.message);
          }

          dispatch(loginSuccess(session));
          props.router.navigate(postLoginPath());
          return true;
        }
      }

      if (response.message) {
        toast.success(response.message);
      }

      const returnPath = postLoginPath();
      const selectStudioPath =
        returnPath !== "/dashboard"
          ? `/select-studio?return=${encodeURIComponent(returnPath)}`
          : "/select-studio";
      props.router.navigate(selectStudioPath);
      return true;
    }

    const session = getAuthSession();

    if (response.success && session) {
      if (response.message) {
        toast.success(response.message);
      }

      dispatch(loginSuccess(session));
      props.router.navigate(postLoginPath());
      return true;
    }

    return false;
  };

  const passwordSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string().required(t("auth.login.validation.emailRequired")),
        password: Yup.string().required(
          t("auth.login.validation.passwordRequired")
        ),
      }),
    [t]
  );

  const emailOnlySchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string().required(t("auth.login.validation.emailRequired")),
      }),
    [t]
  );

  const otpSchema = useMemo(
    () =>
      Yup.object({
        code: Yup.string()
          .required(t("auth.login.validation.otpRequired"))
          .matches(/^\d{6}$/, t("auth.login.validation.otpFormat")),
      }),
    [t]
  );

  const otpForm = useFormik({
    enableReinitialize: true,
    initialValues: { code: "" },
    validationSchema: otpSchema,
    onSubmit: async (values) => {
      if (!challengeId) {
        return;
      }

      setLoader(true);
      clearLoginError();
      setShowSpamHint(false);

      try {
        const response = await verifyEmailCode(challengeId, values.code);
        const handled = await finishLogin(response);

        if (!handled) {
          presentLoginError(
            response.message ||
              response.error?.message ||
              t("auth.login.toast.otpError"),
            resolveErrorAction(response.error?.code)
          );
        }
      } catch (error: unknown) {
        const resolved = resolveAuthClientError(
          error,
          t("auth.login.toast.otpError")
        );
        const action = resolveErrorAction(resolved.code, resolved.status);

        if (
          resolved.code === "OTP_EXPIRED" ||
          resolved.code === "OTP_LOCKED"
        ) {
          setResendCooldown(0);
          otpForm.setFieldValue("code", "");
          presentLoginError(
            resolved.code === "OTP_EXPIRED"
              ? t("auth.login.toast.otpExpired")
              : t("auth.login.toast.otpLocked"),
            action
          );
          return;
        }

        if (resolved.code === "OTP_INVALID") {
          otpForm.setFieldValue("code", "");
          presentLoginError(t("auth.login.toast.otpInvalid"), action);
          return;
        }

        presentLoginError(
          isAuthRateLimited(resolved)
            ? t("auth.login.toast.rateLimited")
            : resolved.message,
          action
        );
      } finally {
        setLoader(false);
      }
    },
  });

  const applyOtpChallenge = (payload: {
    challenge_id: string;
    email_hint?: string;
    emailFallback: string;
  }) => {
    setChallengeId(payload.challenge_id);
    setEmailHint(payload.email_hint || payload.emailFallback);
    setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
    setShowSpamHint(true);
    otpForm.resetForm();
    clearLoginError();
  };

  const requestOtpForEmail = async (email: string): Promise<boolean> => {
    const response = await requestEmailCode(email, "login");

    if (response.success && response.data?.challenge_id) {
      applyOtpChallenge({
        challenge_id: response.data.challenge_id,
        email_hint: response.data.email_hint,
        emailFallback: email,
      });
      toast.success(response.message || t("auth.login.toast.otpSent"));
      return true;
    }

    presentLoginError(
      response.message ||
        response.error?.message ||
        t("auth.login.toast.genericError"),
      resolveErrorAction(response.error?.code)
    );
    return false;
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: new URLSearchParams(window.location.search).get("email") || "",
      password: "",
    },
    validationSchema: useEmailCode ? emailOnlySchema : passwordSchema,
    onSubmit: async (values) => {
      setLoader(true);
      clearLoginError();

      try {
        if (useEmailCode) {
          await requestOtpForEmail(values.email);
          return;
        }

        const response = await authLogin(values.email, values.password);
        const handled = await finishLogin(response);

        if (!handled) {
          presentLoginError(
            response.message ||
              response.error?.message ||
              t("auth.login.toast.genericError"),
            resolveErrorAction(response.error?.code)
          );
        }
      } catch (error: unknown) {
        const resolved = resolveAuthClientError(
          error,
          t("auth.login.toast.genericError")
        );
        presentLoginError(
          isAuthRateLimited(resolved)
            ? t("auth.login.toast.rateLimited")
            : resolved.message,
          resolveErrorAction(resolved.code, resolved.status)
        );
      } finally {
        setLoader(false);
      }
    },
  });

  const resetOtpChallenge = () => {
    setChallengeId(null);
    setEmailHint("");
    setResendCooldown(0);
    setShowSpamHint(false);
    otpForm.resetForm();
    clearLoginError();
  };

  const toggleEmailCodeMode = (next: boolean) => {
    setUseEmailCode(next);
    setPreferEmailCode(next);
    clearLoginError();
    validation.setFieldValue("password", "");
    resetOtpChallenge();
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || loader || !validation.values.email) {
      return;
    }

    setLoader(true);
    clearLoginError();

    try {
      await requestOtpForEmail(validation.values.email);
    } catch (error: unknown) {
      const resolved = resolveAuthClientError(
        error,
        t("auth.login.toast.genericError")
      );
      presentLoginError(
        isAuthRateLimited(resolved)
          ? t("auth.login.toast.rateLimited")
          : resolved.message,
        resolveErrorAction(resolved.code, resolved.status)
      );
    } finally {
      setLoader(false);
    }
  };

  const handleOtpInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (showSpamHint) {
      setShowSpamHint(false);
    }
    otpForm.setFieldValue("code", digitsOnly(event.target.value), true);
  };

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (showSpamHint) {
      setShowSpamHint(false);
    }
    otpForm.setFieldValue(
      "code",
      digitsOnly(event.clipboardData.getData("text")),
      true
    );
  };

  useEffect(() => {
    document.title = t("auth.login.pageTitle");
  }, [t]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setResendCooldown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const formTitle = challengeId
    ? t("auth.login.otp.title")
    : t("auth.login.title");
  const formSubtitle = challengeId
    ? t("auth.login.otp.subtitle", { email: emailHint })
    : useEmailCode
      ? t("auth.login.otp.requestSubtitle")
      : t("auth.login.subtitle");

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <AuthSplitLayout
        title={formTitle}
        subtitle={formSubtitle}
        headerAction={
          <Link to="/register" className="adatis-auth-split__header-btn">
            <i className="ri-user-add-line" aria-hidden="true" />
            {t("auth.login.links.signUp")}
          </Link>
        }
        lead={
          <>
            {errorMessage ? (
              <Alert
                color="danger"
                className="mb-0"
                role="alert"
                aria-live="assertive"
              >
                <div>{errorMessage}</div>
                {errorAction === "no_membership" ? (
                  <div className="mt-2 small text-muted">
                    {t("auth.login.errors.noMembershipRetryHint")}
                  </div>
                ) : null}
                {errorAction === "account_not_found" ? (
                  <div className="mt-2 small">
                    <Link
                      to={`/register?email=${encodeURIComponent(
                        validation.values.email || ""
                      )}`}
                      className="fw-semibold text-decoration-underline"
                    >
                      {t("auth.login.errors.signUpCta")}
                    </Link>
                  </div>
                ) : null}
              </Alert>
            ) : null}
            {challengeId && showSpamHint ? (
              <Alert
                color="info"
                className={`${errorMessage ? "mt-2" : ""} mb-0`}
                role="status"
                aria-live="polite"
              >
                {t("auth.login.otp.spamHint")}
              </Alert>
            ) : null}
          </>
        }
      >
        <>
                      {challengeId ? (
                        <Form
                          onSubmit={(event) => {
                            event.preventDefault();
                            otpForm.handleSubmit();
                          }}
                        >
                          <div className="mb-3">
                            <Label htmlFor="login-otp" className="form-label">
                              {t("auth.login.fields.otp.label")}
                            </Label>
                            <Input
                              id="login-otp"
                              name="code"
                              className="form-control text-center fs-4"
                              placeholder={t(
                                "auth.login.fields.otp.placeholder"
                              )}
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              autoComplete="one-time-code"
                              autoFocus
                              maxLength={6}
                              onChange={handleOtpInputChange}
                              onPaste={handleOtpPaste}
                              onBlur={otpForm.handleBlur}
                              value={otpForm.values.code || ""}
                              invalid={
                                !!(otpForm.touched.code && otpForm.errors.code)
                              }
                            />
                            {otpForm.touched.code && otpForm.errors.code ? (
                              <FormFeedback type="invalid">
                                {otpForm.errors.code}
                              </FormFeedback>
                            ) : (
                              <div className="form-text">
                                {t("auth.login.otp.pasteHint")}
                              </div>
                            )}
                          </div>

                          <div className="mt-4">
                            <Button
                              color="primary"
                              disabled={loader}
                              className="btn btn-primary w-100"
                              type="submit"
                            >
                              {loader ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  {t("auth.login.actions.loading")}
                                </>
                              ) : (
                                t("auth.login.actions.verifyOtp")
                              )}
                            </Button>
                          </div>

                          <div className="mt-3 text-center">
                            <button
                              type="button"
                              className="btn btn-link"
                              disabled={loader || resendCooldown > 0}
                              onClick={handleResendCode}
                              aria-live="polite"
                            >
                              {resendCooldown > 0
                                ? t("auth.login.actions.resendIn", {
                                    time: formatCountdown(resendCooldown),
                                  })
                                : t("auth.login.actions.resendCode")}
                            </button>
                          </div>

                          <div className="mt-1 text-center">
                            <button
                              type="button"
                              className="btn btn-link text-muted"
                              onClick={resetOtpChallenge}
                            >
                              {t("auth.login.actions.changeEmail")}
                            </button>
                          </div>
                        </Form>
                      ) : (
                        <Form
                          onSubmit={(event) => {
                            event.preventDefault();
                            validation.handleSubmit();
                          }}
                        >
                          <div className="mb-3">
                            <Label htmlFor="email" className="form-label">
                              {t("auth.login.fields.email.label")}
                            </Label>
                            <Input
                              name="email"
                              className="form-control"
                              placeholder={t(
                                "auth.login.fields.email.placeholder"
                              )}
                              type="email"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email || ""}
                              invalid={
                                !!(
                                  validation.touched.email &&
                                  validation.errors.email
                                )
                              }
                            />
                            {validation.touched.email &&
                            validation.errors.email ? (
                              <FormFeedback type="invalid">
                                {validation.errors.email}
                              </FormFeedback>
                            ) : null}
                          </div>

                          {!useEmailCode ? (
                            <div className="mb-3">
                              <div className="float-end">
                                <Link
                                  to="/forgot-password"
                                  className="text-muted"
                                >
                                  {t("auth.login.actions.forgotPassword")}
                                </Link>
                              </div>
                              <Label
                                className="form-label"
                                htmlFor="password-input"
                              >
                                {t("auth.login.fields.password.label")}
                              </Label>
                              <div className="position-relative auth-pass-inputgroup mb-3">
                                <Input
                                  name="password"
                                  value={validation.values.password || ""}
                                  type={passwordShow ? "text" : "password"}
                                  className="form-control pe-5"
                                  placeholder={t(
                                    "auth.login.fields.password.placeholder"
                                  )}
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  invalid={
                                    !!(
                                      validation.touched.password &&
                                      validation.errors.password
                                    )
                                  }
                                />
                                {validation.touched.password &&
                                validation.errors.password ? (
                                  <FormFeedback type="invalid">
                                    {validation.errors.password}
                                  </FormFeedback>
                                ) : null}
                                <button
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  id="password-addon"
                                  onClick={() =>
                                    setPasswordShow(!passwordShow)
                                  }
                                >
                                  <i className="ri-eye-fill align-middle"></i>
                                </button>
                              </div>
                            </div>
                          ) : null}

                          {!useEmailCode ? (
                            <div className="form-check">
                              <Input
                                className="form-check-input"
                                type="checkbox"
                                value=""
                                id="auth-remember-check"
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="auth-remember-check"
                              >
                                {t("auth.login.actions.rememberMe")}
                              </Label>
                            </div>
                          ) : null}

                          <div className="mt-4">
                            <Button
                              color="primary"
                              disabled={loader}
                              className="btn btn-primary w-100"
                              type="submit"
                            >
                              {loader ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  {t("auth.login.actions.loading")}
                                </>
                              ) : useEmailCode ? (
                                t("auth.login.actions.sendCode")
                              ) : (
                                t("auth.login.actions.submit")
                              )}
                            </Button>
                          </div>

                          <div className="mt-3">
                            <button
                              type="button"
                              className="btn btn-outline-secondary w-100"
                              onClick={() =>
                                toggleEmailCodeMode(!useEmailCode)
                              }
                            >
                              {useEmailCode
                                ? t("auth.login.actions.usePassword")
                                : t("auth.login.actions.useEmailCode")}
                            </button>
                          </div>

                          <div className="mt-3 mb-0">
                            <GoogleAuthButton intent="login" disabled={loader} />
                          </div>
                        </Form>
                      )}
        </>
      </AuthSplitLayout>
    </React.Fragment>
  );
};

export default withRouter(Login);
